import api from './api';
import ErrorHandler from './errorHandler';

/**
 * OfficeSpace API Service
 */
class OfficeSpaceService {
    /**
     * Get all office spaces for current user
     * @returns {Promise<object>} Response with office spaces list
     */
    async getUserOfficeSpaces() {
        try {
            const response = await api.get('/api/office-spaces');
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Get office space by ID
     * @param {number} id - OfficeSpace ID
     * @returns {Promise<object>} Response with office space details
     */
    async getOfficeSpaceById(id) {
        try {
            const response = await api.get(`/api/office-spaces/${id}`);
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Create new office space
     * @param {object} data - Office space data (name, metadata)
     * @returns {Promise<object>} Response with created office space
     */
    async createOfficeSpace(data) {
        try {
            const response = await api.post('/api/office-spaces', data);
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Update office space
     * @param {number} id - OfficeSpace ID
     * @param {object} data - Updated office space data
     * @returns {Promise<object>} Response with updated office space
     */
    async updateOfficeSpace(id, data) {
        try {
            const response = await api.put(`/api/office-spaces/${id}`, data);
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Upload a shop logo image → compressed to ≤100KB on the server → R2.
     * @param {number} id - OfficeSpace ID
     * @param {File} file - image file (jpeg/png/webp)
     * @returns {Promise<object>} { url, bytes }
     */
    async uploadShopLogo(id, file) {
        try {
            const fd = new FormData();
            fd.append('file', file);
            const response = await api.post(`/api/office-spaces/${id}/upload-logo`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Delete office space
     * @param {number} id - OfficeSpace ID
     * @returns {Promise<object>} Response with deletion result
     */
    async deleteOfficeSpace(id) {
        try {
            const response = await api.delete(`/api/office-spaces/${id}`);
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Search users by phone number
     * @param {string} phone - Phone number to search
     * @returns {Promise<object>} Response with matching users
     */
    async searchUsersByPhone(phone) {
        try {
            const response = await api.get('/api/office-spaces/search-users', {
                params: { phone }
            });
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Add operator to office space
     * @param {number} officeSpaceId - OfficeSpace ID
     * @param {number} operatorId - Operator user ID
     * @returns {Promise<object>} Response with result
     */
    async addOperator(officeSpaceId, operatorId) {
        try {
            const response = await api.post(`/api/office-spaces/${officeSpaceId}/operators`, {
                operatorId
            });
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Set / clear how an operator is paid (owner only).
     * @param {{wageType:'fixed'|'percent'|null, wageAmount?:number, wagePeriod?:'day'|'week'|'month'}} wage
     */
    async setOperatorWage(officeSpaceId, operatorId, wage) {
        try {
            return await api.put(`/api/office-spaces/${officeSpaceId}/operators/${operatorId}/wage`, wage);
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Owner creates a NEW operator — step 1: SMS a verification code to the operator's phone.
     * @param {number} officeSpaceId
     * @param {string} phone - 11-digit BD number
     */
    async sendOperatorCreateOtp(officeSpaceId, phone) {
        try {
            return await api.post(`/api/office-spaces/${officeSpaceId}/operators/create/send-otp`, { phone });
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Owner creates a NEW operator — step 2: verify the code, create + attach the operator.
     * @param {number} officeSpaceId
     * @param {{name:string, phone:string, password:string, otp:string}} payload
     */
    async createOperator(officeSpaceId, payload) {
        try {
            return await api.post(`/api/office-spaces/${officeSpaceId}/operators/create`, payload);
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Remove operator from office space
     * @param {number} officeSpaceId - OfficeSpace ID
     * @param {number} operatorId - Operator user ID
     * @returns {Promise<object>} Response with result
     */
    async removeOperator(officeSpaceId, operatorId) {
        try {
            const response = await api.delete(`/api/office-spaces/${officeSpaceId}/operators/${operatorId}`);
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Leave an office space (operator only)
     * @param {number} officeSpaceId - OfficeSpace ID
     * @returns {Promise<object>} Response with result
     */
    async leaveOfficeSpace(officeSpaceId) {
        try {
            const response = await api.post(`/api/office-spaces/${officeSpaceId}/leave`);
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }
}

export default new OfficeSpaceService();
